import { Avatar, MessageBox, NavigationButton, Table } from "@jutro/ui";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { envVars } from "envvars";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { PatientCardNumberTag } from "components/VisitsWithDeletedShiftTable/PatientCardNumberTag";
import { tileModeMessageBoxContentMap } from "components/VisitsWithDeletedShiftTable/tools";
import { ConfirmDialog } from "components/new/ConfirmDialog";
import {
  EdmMedicalAppVisitType,
  EdmMedicalPerson,
} from "lib/graphql/megaSchema";
import {
  defaultWorkspaceTilesMap,
  workspaceTilesAtom,
} from "lib/tools/createWorkspace/atoms";
import { isDataTile } from "lib/tools/createWorkspace/tools";
import { TileColumn } from "lib/tools/createWorkspace/types";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";
import { avatarConfig } from "lib/tools/httpConfigs";
import { visitTypeLabelMap } from "views/StaffSchedule/tools";
import { VisitsWithDeletedShiftData } from "./types";
import { PastelRainbow } from "components/VisitsWithDeletedShiftTable/PastelRainbow";

type Props = {
  data: VisitsWithDeletedShiftData;
};

type Row = VisitsWithDeletedShiftData["visits"][number];

const columns: ColumnDef<Row>[] = [
  {
    id: "date",
    accessorKey: "scheduledOn",
    header: "Data wizyty",
    cell: ({ getValue }) => (
      <span className="font-label">
        {dayjs(getValue<string>()).format("DD MMM YYYY")}
      </span>
    ),
  },
  {
    id: "startTime",
    accessorKey: "scheduledOn",
    header: "Godzina",
    cell: ({ getValue }) => (
      <span className="font-label">
        {dayjs(getValue<string>()).format("HH:mm")}
      </span>
    ),
  },
  {
    accessorKey: "staff",
    header: "Wcześniej przypisana do",
    cell: ({ getValue }) => {
      const { firstname, lastname, id } = getValue<EdmMedicalPerson>();

      return (
        <div className="flex w-fit min-w-64 items-center gap-3">
          <Avatar
            type="photo"
            imgSrc={`${avatarConfig[envVars.REACT_APP_CONFIG]}/${id}`}
          />
          <span className="font-label">
            {firstname} {lastname}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Rodzaj slotu",
    cell: ({ getValue }) => (
      <span className="font-label">
        {visitTypeLabelMap[getValue<EdmMedicalAppVisitType>()]}
      </span>
    ),
  },
  {
    accessorKey: "patientId",
    header: "Karta pacjenta",
    cell: ({ getValue }) => (
      <PatientCardNumberTag patientId={getValue<string>()} />
    ),
  },
];

export const VisitsWithDeletedShiftTable = ({ data }: Props) => {
  const { mode, visits } = data;
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const [workspaceTiles] = useAtom(workspaceTilesAtom);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { addTile, removeTile, visibleTiles } = useTileSystem();

  useEffect(() => {
    if (
      visibleTiles.some(
        (tile) => isDataTile(tile) && tile.name === "schedule-visit-form",
      )
    ) {
      return;
    }

    setSelectedRow(null);
  }, [workspaceTiles]);

  const [, setWorkspaceTiles] = useAtom(workspaceTilesAtom);

  const handleCloseTile = () => {
    setWorkspaceTiles(
      defaultWorkspaceTilesMap["staff-scheduler"].map((tile) => [tile]),
    );
  };

  return (
    <>
      <div className="flex h-full flex-col gap-4 rounded-lg bg-white p-4">
        <div className="flex justify-between">
          <h4 className="font-heading-4">Wizyty bez personelu</h4>
          <NavigationButton
            onClick={() => {
              if (mode === "afterDeletedShift" && visits.length > 0) {
                setIsConfirmModalOpen(true);
                return;
              }
              handleCloseTile();
            }}
          />
        </div>

        {visits.length > 0 ? (
          <>
            <MessageBox {...tileModeMessageBoxContentMap[mode]} />

            <div className="overflow-x-scroll">
              <Table
                columns={columns}
                data={visits}
                selectionRowMode="single"
                selectedRow={selectedRow}
                setSelectedRow={(row) => {
                  if (selectedRow) {
                    removeTile(selectedRow.referenceOldVisitId);
                  }

                  setSelectedRow(row);

                  if (row) {
                    const visitData = row as Row;

                    addTile(
                      {
                        id: visitData.referenceOldVisitId,
                        name: "schedule-visit-form",
                        data: {
                          id: visitData.referenceOldVisitId,
                          mode: "edit",
                          patientId: visitData.patientId,
                        },
                      },
                      TileColumn.Right,
                    );
                  }
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-8">
            <PastelRainbow />

            <p className="font-paragraph-0 text-black">
              Brawo! Wszystkie wizyty zostały już przypisane
            </p>
          </div>
        )}
      </div>
      <ConfirmDialog
        title="Czy na pewno chcesz zamknąć listę przed edycją wszystkich wizyt bez personelu?"
        text="Po zamknięciu tego widoku nie zobaczysz tej listy ponownie."
        confirmText="Zamknij mimo to"
        open={isConfirmModalOpen}
        setOpen={setIsConfirmModalOpen}
        onConfirm={() => handleCloseTile()}
      />
    </>
  );
};
